import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const meta = {
  title: "Drunk Driving Fatalities by State",
  description: "New data reveals the states with the most alcohol-impaired driving deaths. See where your state stands.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/HD_1980x1080-scaled.webp",
    alt: "Drunk Driving Fatalities by State: Where the Risk Is Highest"
  },
  author: {
    name: "Joe Mercurio",
    role: "Author",
    image: ""
  },
  publishedAt: "August 9, 2025",
  updatedAt: "August 18, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    img: "img",
    li: "li",
    ol: "ol",
    p: "p",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.p, {
      children: "Drunk driving remains one of the deadliest risks on the road. Using the latest data from the National Highway Traffic Safety Administration, we analyzed where drunk driving fatalities are most common, which states have improved, and where the problem is getting worse."
    }), "\n", _jsxs(_components.p, {
      children: ["To do this, we calculated the rate of alcohol-impaired driving deaths per 100,000 residents in each U.S. state. These accidents involved at least one driver with a ", _jsx(_components.a, {
        href: "https://my.clevelandclinic.org/health/diagnostics/22689-blood-alcohol-content-bac",
        children: "Blood Alcohol Concentration"
      }), " of 0.08 or higher — the legal limit in the United States. We then compared these figures to 2020 data to reveal where fatal crashes are rising or falling."]
    }), "\n", _jsxs(_components.p, {
      children: ["Understanding these risks is important for public safety awareness, but it also has widespread impacts for even the safest drivers (for instance, ", _jsx(_components.a, {
        href: "/auto-insurance/full-coverage-car-insurance",
        children: "car insurance"
      }), " companies use local crash data when setting premiums). In areas with more drunk driving deaths, drivers may pay higher premiums, even if their own records are clean. Living in a high-risk state can affect both your safety and your coverage costs."]
    }), "\n", _jsx(_components.h2, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "South Carolina has the most drunk driving fatalities per 100,000 residents (7.1), followed by Montana (5.9) and Arizona (5.2)."
      }), "\n", _jsx(_components.li, {
        children: "New Jersey has the fewest (1.4), followed by Utah (1.5) and New York (1.5)."
      }), "\n", _jsx(_components.li, {
        children: "25 states have seen a decrease in drunk driving fatalities, with Maine seeing the largest with a 38.6% drop since 2020. North Carolina fatalities have also decreased by 6.8%."
      }), "\n", _jsx(_components.li, {
        children: "Arizona has seen the biggest increase in drunk driving fatalities, with a 43.5% increase since 2020."
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Which States Have the Most Drunk Driving Fatalities?"
    }), "\n", _jsx(_components.p, {
      children: "While drunk driving is a nationwide issue, the frequency and severity of fatal crashes vary significantly by state. This map shows the rate of alcohol-impaired driving deaths in each state by the number of fatalities per 100,000 residents. In 2023, South Carolina had the highest rate of drunk driving fatalities in the country, with 7.1 deaths per capita. Montana and Arizona were next with 5.9 and 5.2, respectively."
    }), "\n", _jsx("iframe", {
      className: "wp-embedded-content",
      sandbox: "allow-scripts",
      security: "restricted",
      title: "Interactive or visual content",
      src: "https://flo.uri.sh/visualisation/24444383/embed#?secret=aaAGwBT1hc",
      "data-secret": "aaAGwBT1hc",
      frameBorder: "0",
      scrolling: "no",
      height: "575",
      width: "700"
    }), "\n", _jsx(_components.h4, {
      children: "States With the Most Drunk Driving Fatalities per 100,000 Residents"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "South Carolina (7.1)"
      }), "\n", _jsx(_components.li, {
        children: "Montana (5.9)"
      }), "\n", _jsx(_components.li, {
        children: "Arizona (5.2)"
      }), "\n", _jsx(_components.li, {
        children: "Wyoming (5.1)"
      }), "\n", _jsx(_components.li, {
        children: "Alabama (5.0)"
      }), "\n", _jsx(_components.li, {
        children: "New Mexico (5.0)"
      }), "\n", _jsx(_components.li, {
        children: "Texas (5.0)"
      }), "\n", _jsx(_components.li, {
        children: "Mississippi (4.7)"
      }), "\n", _jsx(_components.li, {
        children: "Arkansas (4.7)"
      }), "\n", _jsx(_components.li, {
        children: "Tennessee (4.6)"
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "States With the Fewest Drunk Driving Fatalities per 100,000 Residents"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "New Jersey (1.4)"
      }), "\n", _jsx(_components.li, {
        children: "Utah (1.5)"
      }), "\n", _jsx(_components.li, {
        children: "New York (1.5)"
      }), "\n", _jsx(_components.li, {
        children: "Massachusetts (1.5)"
      }), "\n", _jsx(_components.li, {
        children: "Minnesota (1.6)"
      }), "\n", _jsx(_components.li, {
        children: "Rhode Island (2.0)"
      }), "\n", _jsx(_components.li, {
        children: "Alaska (2.1)"
      }), "\n", _jsx(_components.li, {
        children: "Vermont (2.3)"
      }), "\n", _jsx(_components.li, {
        children: "Pennsylvania (2.3)"
      }), "\n", _jsx(_components.li, {
        children: "New Hampshire (2.4)"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "New Jersey reported the lowest rate at 1.4 deaths per 100,000 residents, followed closely by Utah (1.5) and New York (1.5). These states may benefit from stronger enforcement, better public transit options, or more effective public safety campaigns. North Carolina sat in the middle of the pack with a rate of 3.5."
    }), "\n", _jsx(_components.h3, {
      children: "States Where Drunk Driving Deaths Are Increasing"
    }), "\n", _jsx(_components.p, {
      children: "Looking at year-over-year change adds another layer to the story. While some states have made progress in reducing drunk driving fatalities, others have seen sharp increases since 2020."
    }), "\n", _jsx("iframe", {
      className: "wp-embedded-content",
      sandbox: "allow-scripts",
      security: "restricted",
      title: "Interactive or visual content",
      src: "https://flo.uri.sh/visualisation/24444679/embed#?secret=tcqYB7rAKE",
      "data-secret": "tcqYB7rAKE",
      frameBorder: "0",
      scrolling: "no",
      height: "575",
      width: "700"
    }), "\n", _jsx(_components.h4, {
      children: "States With the Highest Increases in Drunk Driving Fatalities"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "Arizona (43.5%)"
      }), "\n", _jsx(_components.li, {
        children: "Delaware (38.5%)"
      }), "\n", _jsx(_components.li, {
        children: "Alaska (36.4%)"
      }), "\n", _jsx(_components.li, {
        children: "Kansas (35.3%)"
      }), "\n", _jsx(_components.li, {
        children: "Hawaii (34.6%)"
      }), "\n", _jsx(_components.li, {
        children: "South Carolina (32.1%)"
      }), "\n", _jsx(_components.li, {
        children: "Washington (30.0%)"
      }), "\n", _jsx(_components.li, {
        children: "Nevada (27.9%)"
      }), "\n", _jsx(_components.li, {
        children: "Alabama (21.5%)"
      }), "\n", _jsx(_components.li, {
        children: "Iowa (15.8%)"
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "States With the Biggest Decreases in Drunk Driving Fatalities"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "Maine (-38.6%)"
      }), "\n", _jsx(_components.li, {
        children: "West Virginia (-29.0%)"
      }), "\n", _jsx(_components.li, {
        children: "Rhode Island (-21.4%)"
      }), "\n", _jsx(_components.li, {
        children: "South Dakota (-21.3%)"
      }), "\n", _jsx(_components.li, {
        children: "Montana (-20.2%)"
      }), "\n", _jsx(_components.li, {
        children: "Wyoming (-18.9%)"
      }), "\n", _jsx(_components.li, {
        children: "Wisconsin (-14.3%)"
      }), "\n", _jsx(_components.li, {
        children: "New Mexico (-11.8%)"
      }), "\n", _jsx(_components.li, {
        children: "Missouri (-11.1%)"
      }), "\n", _jsx(_components.li, {
        children: "Utah (-10.5%)"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "Arizona had the most dramatic increase, with drunk driving fatalities rising by 43.5% over three years. Delaware and Alaska also saw significant rises, with increases of 38.5% and 36.4%, respectively. Other states showing big upticks included Kansas, Hawaii, and South Carolina (all over 30%)."
    }), "\n", _jsx(_components.p, {
      children: "On a more positive note, 25 states showed a decline in drunk driving deaths. Maine led with a 38.6% drop, followed by West Virginia (-29%) and Rhode Island (-21.4%). Even states like Montana and Wyoming, which still rank high in fatality rates, saw double-digit decreases, suggesting some improvement despite ongoing risk. North Carolina also showed a 6.8% decrease, reinforcing a positive trend even as certain counties within the state continue to face challenges."
    }), "\n", _jsx(_components.h3, {
      children: "North Carolina’s Most Impacted Counties For Drunk Driving"
    }), "\n", _jsx(_components.p, {
      children: "While statewide data offers a broad picture, local trends can show where drunk driving hits hardest. Guardian Service is based in Raleigh, so we wanted to take a deep dive into how North Carolina is tackling drunk driving in the state. Some counties stood out for having higher numbers of alcohol-impaired driving fatalities."
    }), "\n", _jsx(_components.p, {
      children: _jsx(_components.img, {
        src: "/images/blog/Asset-3.webp",
        alt: "Infographic shows a chart ranking the top 10 North Carolina counties with the highest drunk driving fatalities in 2023, led by Mecklenburg (34) and Wake (30), with a total of 381 fatalities statewide."
      })
    }), "\n", _jsx(_components.p, {
      children: "In 2023, there were 381 drunk driving deaths across North Carolina. The counties with the highest totals were:"
    }), "\n", _jsxs(_components.ol, {
      children: ["\n", _jsx(_components.li, {
        children: "Mecklenburg County (home to Charlotte): 34"
      }), "\n", _jsx(_components.li, {
        children: "Wake County (home to Raleigh): 30"
      }), "\n", _jsx(_components.li, {
        children: "Guilford County (home to Greensboro): 18"
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "These counties are among the most populous in the state, which contributes to higher traffic volumes and, potentially, more alcohol-related crashes. Still, the numbers suggest a need for targeted awareness and enforcement efforts in high-impact areas."
    }), "\n", _jsx(_components.h3, {
      children: "Conclusion: What This Means for Drivers"
    }), "\n", _jsx(_components.p, {
      children: "Drunk driving remains a serious threat, and its impact can vary widely depending on where you live. Some states have successfully curbed this issue, while others are dealing with a growing problem. For drivers in high-risk states with rising fatality trends, it’s especially important to be vigilant and prepared for the unexpected while on the road. That includes not just driving responsibly but also carrying the right car insurance in case you’re ever affected by someone else’s poor choices."
    }), "\n", _jsxs(_components.p, {
      children: ["In areas with higher accident rates, ", _jsx(_components.a, {
        href: "/auto-insurance/auto-insurance-the-state-of-auto-insurance-in-2025",
        children: "insurance premiums"
      }), " may reflect increased risk. But coverage also matters when it comes to protecting yourself financially after a crash, whether that means paying for repairs, covering medical costs, or handling legal claims. The more you know about your region’s risks and the coverage provided by your policy, the better equipped you’ll be behind the wheel."]
    }), "\n", _jsx(_components.h4, {
      children: "Methodology"
    }), "\n", _jsxs(_components.p, {
      children: ["We analyzed the most recent publicly available data from the National Highway Traffic Safety Administration’s (NHTSA) ", _jsx(_components.a, {
        href: "https://www.nhtsa.gov/research-data/fatality-analysis-reporting-system-fars",
        children: "Fatality Analysis Reporting System"
      }), " (FARS), using the 2023 dataset to assess the impact of alcohol-impaired driving across U.S. states."]
    }), "\n", _jsxs(_components.p, {
      children: ["To measure the prevalence of drunk driving fatalities, we focused on crashes involving at least one driver with a Blood Alcohol Concentration (BAC) of 0.08 grams per deciliter (g/dL) or higher. This BAC level represents the legal threshold for alcohol impairment in the United States, as defined by the ", _jsx(_components.a, {
        href: "https://www.nhtsa.gov/risky-driving/drunk-driving",
        children: "NHTSA"
      }), ". Any fatality involving a driver at or above this level is classified as alcohol-impaired under federal standards."]
    }), "\n", _jsx(_components.p, {
      children: "Our analysis calculated the rate of alcohol-impaired driving fatalities per 100,000 residents in each state. We also compared the 2023 data to figures from 2020 to identify trends and highlight where drunk driving deaths are increasing."
    }), "\n", _jsx(_components.h4, {
      children: "About Guardian Service"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://guardianservice.com/",
        children: "Guardian Service"
      }), " makes car insurance simpler, faster, and less frustrating. Unlike traditional providers, our zero-commission agents help you compare top-rated plans, switch providers easily, and even re-shop your policy each year to make sure you’re always getting the best deal. Whether you’re looking for basic coverage or a plan with added protection, we help you stay insured without the usual hassle."]
    }), "\n", _jsx(_components.h4, {
      children: "Fair Use Statement"
    }), "\n", _jsxs(_components.p, {
      children: ["We encourage the noncommercial sharing of this content for educational or public awareness purposes. If you reference this study, please link back to ", _jsx(_components.a, {
        href: "https://guardianservice.com/",
        children: "Guardian Service"
      }), " and credit us as the original source."]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
